/**
 * Application Constants
 * Centralized constants for the PMPI application
 */

// Filter Constants
export const VIEW_MODES = {
  BOARD: 'board',
  LIST: 'list',
  CALENDAR: 'calendar',
  GANTT: 'gantt',
  NOTIFICATIONS: 'notifications',
  REPORTS: 'reports'
};

export const FILTER_VALUES = {
  ALL: 'all'
};

// Pagination Constants
export const PAGINATION = {
  DEFAULT_LIMIT: 10,
  DEFAULT_PAGE: 1
};

// Task Status Constants
export const TASK_STATUS = {
  CLOSED: 'Closed',
  IN_PROGRESS: 'In Progress',
  BLOCKED: 'Blocked',
  TO_DO: 'To Do'
};

// Task Type Constants
export const TASK_TYPE = {
  TASK: 'Task'
};

export const UI_LABELS = {
  NOT_SET: 'Not set'
};


// Toast Messages
export const TOAST_MESSAGES = {

  SPRINT_CREATED: 'Sprint created successfully',
    SPRINT_STATUS_UPDATED: 'Sprint status updated',
  
  COLUMN_CREATED: 'Column created successfully!',
  COLUMN_CREATE_FAILED: 'Failed to create column. Please try again.',
  PROJECT_CREATED: 'Project created successfully!',
  PROJECT_CREATE_FAILED: 'Failed to create project. Please try again.',
  PROJECT_UPDATE_FAILED: 'Failed to update project. Please try again.',
  PROJECT_UPDATED: 'Project updated successfully!',
  PROJECT_DELETE_FAILED: 'Failed to delete project. Please try again.',
  PROJECT_DELETED: 'Project deleted successfully!',
  USER_CREATE_FAILED: 'Failed to create user. Please try again.',
  USER_UPDATE_FAILED: 'Failed to update user. Please try again.',
  TICKET_ATTRIBUTE_DELETE_FAILED: 'Failed to delete ticket attribute. Please try again.',
  LABEL_CREATE_FAILED: 'Failed to create label. Please try again.',
  LABEL_UPDATE_FAILED: 'Failed to update label. Please try again.',
  LABEL_NAME_EXISTS: 'A label with this name already exists.',
  PRIORITY_CREATE_FAILED: 'Failed to create priority. Please try again.',
  PRIORITY_UPDATE_FAILED: 'Failed to update priority. Please try again.',
  PRIORITY_NAME_EXISTS: 'A priority with this name already exists.',
  TASK_TYPE_CREATE_FAILED: 'Failed to create task type. Please try again.',
  TASK_TYPE_UPDATE_FAILED: 'Failed to update task type. Please try again.',
  TASK_TYPE_NAME_EXISTS: 'A task type with this name already exists.',
  SPRINT_CREATE_FAILED: 'Failed to create sprint. Please try again.',
  SPRINT_UPDATE_FAILED: 'Failed to update sprint. Please try again.',
  SPRINT_NAME_EXISTS: 'A sprint with this name already exists.',
  TICKET_ATTRIBUTE_CREATE_FAILED: 'Failed to create ticket attribute. Please try again.',
  TICKET_ATTRIBUTE_UPDATE_FAILED: 'Failed to update ticket attribute. Please try again.',
};

export const PROJECT = {
  LEAD: 'lead',
  SPACE: 'space'
};

export const PROJECT_TYPES = {
  SCRUM: 'Scrum',
  KANBAN: 'Kanban'
};

export const SPRINT_SETTINGS_CONFIG = {
  STORY_POINT_OPTIONS: [40, 50, 60, 70],
  MIN_STORY_POINTS: 1,
  MAX_STORY_POINTS: 200
};

export const SCRUM_ROLES = {
  SCRUM_MASTER: 'Scrum Master',
  PRODUCT_OWNER: 'Product Owner',
  SCRUM_TEAM: 'Scrum Team'
};

export const BACKEND_SCRUM_ROLES = {
  SCRUM_MASTER: 'SCRUM_MASTER',
  PRODUCT_OWNER: 'PRODUCT_OWNER',
  SCRUM_TEAM: 'SCRUM_TEAM'
};

// Button Text Constants
export const BUTTON_TEXT = {
  RESET_PASSWORD: 'Reset Password',
  RESETTING_PASSWORD: 'Resetting Password...',
  CREATING: 'Creating...',
  DELETING: 'Deleting...'
};

// Validation Messages
export const VALIDATION_MESSAGES = {
  FIRST_NAME_REQUIRED: 'First name is required',
  LAST_NAME_REQUIRED: 'Last name is required',
  EMAIL_REQUIRED: 'Email is required',
  VALID_EMAIL_REQUIRED: 'Please enter a valid email address',
  PREFIX_MIN_LENGTH: 'Prefix must be at least 2 characters long.',
  PREFIX_ALREADY_EXISTS: 'This prefix already exists. Please choose a different one.'
};

// Form Field Names
export const FORM_FIELDS = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  EMAIL: 'email',
  ROLE: 'role'
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  PM: 'pm'
};

export const PROJECT_ADMIN_ROLES = ['PM', 'SM', 'PO', 'ADMIN', 'PRODUCT OWNER', 'SCRUM MASTER', 'PROJECT MANAGER'];

// Navigation Items
export const NAV_ITEMS = {
  DASHBOARD: 'dashboard',
  USER_MANAGEMENT: 'user-management',
  SPACE_MANAGEMENT: 'space-management',
  PROJECT_MANAGEMENT: 'project-management'
};

// Route Constants
export const ROUTES = {
  FORGOT_PASSWORD: '/forgot-password',
};

// Date Range Constants
export const DATE_RANGES = {
  SELECT_TIME: 'selectTime',
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  THIS_WEEK: 'thisWeek',
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  LAST_7_DAYS: 'last7Days',
  LAST_14_DAYS: 'last14Days',
  LAST_30_DAYS: 'last30Days',
  LAST_60_DAYS: 'last60Days',
  LAST_90_DAYS: 'last90Days',
  LAST_120_DAYS: 'last120Days',
  LAST_180_DAYS: 'last180Days',
  CUSTOM: 'custom'
};

export const DATE_RANGE_LABELS = {
  [DATE_RANGES.SELECT_TIME]: 'Select Time',
  [DATE_RANGES.TODAY]: 'Today',
  [DATE_RANGES.YESTERDAY]: 'Yesterday',
  [DATE_RANGES.THIS_WEEK]: 'This Week',
  [DATE_RANGES.THIS_MONTH]: 'This Month',
  [DATE_RANGES.LAST_MONTH]: 'Last Month',
  [DATE_RANGES.LAST_7_DAYS]: 'Last 7 Days',
  [DATE_RANGES.LAST_14_DAYS]: 'Last 14 Days',
  [DATE_RANGES.LAST_30_DAYS]: 'Last 30 Days',
  [DATE_RANGES.LAST_60_DAYS]: 'Last 60 Days',
  [DATE_RANGES.LAST_90_DAYS]: 'Last 90 Days',
  [DATE_RANGES.LAST_120_DAYS]: 'Last 120 Days',
  [DATE_RANGES.LAST_180_DAYS]: 'Last 180 Days',
  [DATE_RANGES.CUSTOM]: 'Custom Range'
};

export const CUSTOM_SELECTED = 'customSelected';

export const DATE_FORMAT_OPTIONS = {
  year: "numeric",
  month: "short", // "Aug"
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true // for AM/PM
};

// Recurrence values must match the frequency and weekday values accepted by the ticket APIs.
export const RECURRENCE_FREQUENCIES = {
  DAILY: 'DAILY',
  ALTERNATE_DAY: 'ALTERNATE_DAY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY'
};

// Daily Standup reminder time options
export const REMINDER_TIME_OPTIONS = [
  { value: '15min', label: '15 min' },
  { value: '30min', label: '30 min' },
  { value: '45min', label: '45 min' },
  { value: '1h',    label: '1 hr' },
  { value: '2h',    label: '2 hr' },
];

export const DEFAULT_REMINDER_TIME = '1h';

// The array order matches JavaScript Date#getDay(), where Sunday is index 0.
export const WEEKDAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY'
];

// Bulk Action Constants
export const BULK_ACTION_ITEMS = {
  EDIT: {
    id: 'edit',
    label: 'dashboard.bulkActions.editStatus',
    action: 'edit',
    color: 'blue'
  },
  DELETE: {
    id: 'delete', 
    label: 'dashboard.bulkActions.deleteStatus',
    action: 'delete',
    color: 'red'
  },
  BULK_ACTIONS: {
    id: 'bulk-actions',
    label: 'dashboard.bulkActions.actions', 
    action: 'bulk',
    color: 'purple'
  }
};

// Bulk Edit Modal Constants
export const BULK_EDIT_MODAL = {
  INITIAL_VISIBLE_COUNT: 10
};

// Comment reply UI
export const COMMENT_REPLY = {
  AVATAR_STACK_MAX: 3,
  AVATAR_SIZE: 22
};

// Notification Settings Constants
export const NOTIFICATION_SETTINGS = (() => {
  const CHANNELS = {
    EMAIL: 'email',
    IN_APP: 'in_app'
  };

  const TABLE_ROWS = [
    { id: 'task_assigned', labelKey: 'notificationSettings.table.taskAssigned' },
    { id: 'comment_on_task', labelKey: 'notificationSettings.table.commentOnTask' },
    { id: 'reply_to_comment', labelKey: 'notificationSettings.table.replyToComment' },
    { id: 'mention_user', labelKey: 'notificationSettings.table.mentionUser' },
    { id: 'task_status_change', labelKey: 'notificationSettings.table.taskStatusChange' },
    { id: 'worklog_update', labelKey: 'notificationSettings.table.worklogActivity' }
  ];

  const DEFAULT_CHANNEL_OPTIONS = [
    { value: CHANNELS.EMAIL, labelKey: 'notificationSettings.defaults.email' },
    { value: CHANNELS.IN_APP, labelKey: 'notificationSettings.defaults.inApp' }
  ];

  const NOTIFICATION_ENABLED_OPTIONS = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
  ];

  const MUTE_OPTIONS = [
    { value: 'none', labelKey: 'notificationSettings.mute.options.none' },
    { value: '30m', labelKey: 'notificationSettings.mute.options.thirtyMinutes' },
    { value: '1h', labelKey: 'notificationSettings.mute.options.oneHour' },
    { value: '4h', labelKey: 'notificationSettings.mute.options.fourHours' },
    { value: 'tomorrow', labelKey: 'notificationSettings.mute.options.untilTomorrow' },
    { value: 'next_week', labelKey: 'notificationSettings.mute.options.untilNextWeek' }
  ];

  const DEFAULT_CHANNEL = CHANNELS.IN_APP;
  const DEFAULT_MUTE = MUTE_OPTIONS[0].value;
  const DEFAULT_DISABLED_NOTIFICATION_ROWS = new Set([
    'comment_on_task',
    'reply_to_comment',
    'task_status_change',
    'worklog_update'
  ]);

  const createDefaultChannelConfig = () => (
    TABLE_ROWS.reduce((acc, row) => {
      acc[row.id] = {
        [CHANNELS.EMAIL]: !DEFAULT_DISABLED_NOTIFICATION_ROWS.has(row.id),
        [CHANNELS.IN_APP]: !DEFAULT_DISABLED_NOTIFICATION_ROWS.has(row.id)
      };
      return acc;
    }, {})
  );

  const createDefaultPreferences = () => ({
    notification_setting: createDefaultChannelConfig(),
    notification_enabled: true,
    silent_notification_sound: false,
    default_notification: [CHANNELS.EMAIL, CHANNELS.IN_APP],
    muted_notification_for_next: null
  });

  return {
    CHANNELS,
    DEFAULT_CHANNEL_OPTIONS,
    NOTIFICATION_ENABLED_OPTIONS,
    MUTE_OPTIONS,
    TABLE_ROWS,
    PREFERENCE_KEY: 'user_notifications',
    PREFERENCE_FIELDS: {
      NOTIFICATION_SETTING: 'notification_setting',
      NOTIFICATION_ENABLED: 'notification_enabled',
      SILENT_NOTIFICATION_SOUND: 'silent_notification_sound',
      DEFAULT_NOTIFICATION: 'default_notification',
      MUTED_NOTIFICATION_FOR_NEXT: 'muted_notification_for_next'
    },
    DEFAULTS: {
      DEFAULT_CHANNEL,
      DEFAULT_MUTE
    },
    createDefaultChannelConfig,
    createDefaultPreferences
  };
})();

export const NOTIFICATION_BADGE_THRESHOLDS = {
  NINE: 9,
  TEN: 10,
  TWENTY: 20,
  THIRTY: 30,
  FIFTY: 50,
  HUNDRED: 100
};

export const NOTIFICATION_GROUP_BY = {
  TASK: 'task',
  COMMENT: 'comment',
  MENTION: 'mention'
};

export const NOTIFICATION_SORT = {
  OLDEST: 'oldest',
  RECENT: 'recent'
};

export const NOTIFICATION_FILTERS = {
  TASK_ASSIGNMENT: 'task_assignment',
  COMMENT: 'comment',
  MENTION: 'mention'
};

export const NOTIFICATION_EVENT_TYPES = {
  COMMENT: 'COMMENT',
  COMMENT_REPLY: 'COMMENT_REPLY',
  COMMENT_MENTION: 'COMMENT_MENTION',
  DESC_MENTION: 'DESC_MENTION',
  ASSIGNMENT: 'ASSIGNMENT',
  STATUS_CHANGE: 'STATUS_CHANGE',
  WORKLOG: 'WORKLOG',
  SPRINT_CREATED: 'SPRINT_CREATED',
  STANDUP_NOT_UPDATED_REMINDER: 'STANDUP_NOT_UPDATED_REMINDER',
  TASK_STAGNATION_REMINDER: 'TASK_STAGNATION_REMINDER',
  REMINDER_TICKET: 'REMINDER_TICKET'
};

export const NOTIFICATION_SCREEN = {
  GROUP_BY: NOTIFICATION_GROUP_BY,
  SORT: NOTIFICATION_SORT,
  FILTERS: NOTIFICATION_FILTERS,
  TABS: {
    UNREAD: 'unread',
    CLEARED: 'cleared'
  },
  GROUP_BY_OPTIONS: [
    { value: NOTIFICATION_GROUP_BY.TASK, labelKey: 'notifications.groupBy.options.task' },
    { value: NOTIFICATION_GROUP_BY.COMMENT, labelKey: 'notifications.groupBy.options.comment' },
    { value: NOTIFICATION_GROUP_BY.MENTION, labelKey: 'notifications.groupBy.options.mention' }
  ],
  SORT_OPTIONS: [
    { value: NOTIFICATION_SORT.RECENT, labelKey: 'notifications.sort.options.recent' },
    { value: NOTIFICATION_SORT.OLDEST, labelKey: 'notifications.sort.options.oldest' }
  ],
  FILTER_OPTIONS: [
    { value: NOTIFICATION_FILTERS.TASK_ASSIGNMENT, labelKey: 'notifications.filters.assignee' },
    { value: NOTIFICATION_FILTERS.COMMENT, labelKey: 'notifications.filters.comments' },
    { value: NOTIFICATION_FILTERS.MENTION, labelKey: 'notifications.filters.mentions' }
  ],
  COMMENT_SUMMARY_MAX_NAMES: 2,
  UNDO_TIMEOUT_MS: 5000 // 5 seconds for undo functionality
};


// Character Limits
export const CHARACTER_LIMITS = {
  MAX_ASSIGNEES: 10,
  MAX_ATTACHMENTS: 20,
  COMMENT: 10000,
  TICKET_TITLE: 255,
  TICKET_DESCRIPTION: 10000,
  SPACE_DESCRIPTION: 500,
  PROJECT_DESCRIPTION: 500,
  USER_FIRST_NAME: 100,
  USER_LAST_NAME: 100,
  USER_EMAIL: 200,
  LABEL_NAME: 25,
  TASK_TYPE_NAME: 25,
  PRIORITY_NAME: 25,
  SPRINT_NAME: 25,
  SCRUM_SPRINT_NAME: 100,
  SCRUM_SPRINT_GOAL: 250,
  SCRUM_SPRINT_VERSION: 15,
  SPACE_NAME: 75,
  PROJECT_NAME: 75,
  BRANCH_FORMAT: 200,
  BOT_NAME: 100,
  REPO_NAME: 250,
  REPO_URL: 250,
  BASE_BRANCH_NAME: 250,
  PIE_CHART_LABEL_TRUNCATE_LIMIT: 9,
  CALENDAR_NOTE: 250,
  ALERT_NAME: 75
};

// AI Task Planner Phase Constants
export const AI_PHASES = {
  PLANNING: 'planning',
  APPROVAL: 'approval',
  CODE_GENERATION: 'code_generation'
};

export const AI_PHASE_NAMES = {
  [AI_PHASES.PLANNING]: 'Plan Generation',
  [AI_PHASES.APPROVAL]: 'Approval Details',
  [AI_PHASES.CODE_GENERATION]: 'Code Generation Status and PR Details'
};

// Project AI Setting Status Constants
export const PROJECT_AI_SETTING_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

// Generic String Constants
export const GENERIC_STRINGS = {
  ALL: 'all',
  ADD: 'add',
  REMOVE: 'remove',
  UPDATE: 'update'
};

// AI Task Planner Constants
export const AI_TASK_PLANNER = {
  EXPANDED_ALL: 'all'
};

// AI Prompt Tracker Constants
export const AI_PROMPT_PHASES = {
  PLANNING: 'planning',
  CODE_GENERATION: 'code_generation',
  CONTEXT_INITIALIZATION: 'context_initialization'
};

// Project Config Status Constants
export const PROJECT_CONFIG_STATUS = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  COMPLETED: 'completed',
  INACTIVE: 'inactive'
};

// Pull Request Status Constants
export const PR_STATUS = {
  OPEN: 'OPEN',
  MERGED: 'MERGED',
  CLOSED: 'CLOSED'
};

// AI Status Constants
export const AI_STATUS = {
  READY: 'ready',
  PENDING: 'pending',
  FAILED: 'failed',
  IN_PROGRESS: 'in progress',
  GENERATING: 'generating',
  APPROVED: 'approved',
  COMPLETED: 'completed',
  COMPLETE: 'complete'
};

// AI Plan Approval Status Constants
export const AI_PLAN_STATUS = {
  APPROVED: 'APPROVED',
  PENDING: 'PENDING',
  READY: 'READY'
};

// URL Constants
export const URL_PATTERNS = {
  GITHUB_BASE: 'https://github.com/',
  GITHUB_REPO_REGEX: /^https:\/\/github\.com\/[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+\/?$/,
  GIT_EXTENSION: '.git'
};

// Context Files Constants
export const CONTEXT_FILES = [
  { 
    name: 'document.md', 
    description: 'High-level project documentation. Contains project summary, modules, features, terminology, and usage overview.' 
  },
  { 
    name: 'architecture.md', 
    description: 'System architecture file. Includes tech stack, directory structure, data flow diagrams, API structure, and key architectural decisions.' 
  },
  { 
    name: 'ai-rules.md', 
    description: 'Rules and constraints that define how the AI Developer Agent must behave. Includes coding standards, naming conventions, dos & don\'ts, and project-specific guidelines for AI.' 
  },
  { 
    name: 'do-not-touch.json', 
    description: 'A control file that lists all files, folders, and code sections that the AI or UI must not modify. Ensures protected areas of the project remain untouched (e.g., config files, migrations, core utilities).' 
  }
];

// Report Export Constants
export const PDF_EXPORT_CONFIG = {
  PAGE_WIDTH: 210, // A4 width in mm
  PAGE_HEIGHT: 297, // A4 height in mm
  MARGIN: 14, // mm
  SERIAL_NUMBER_COLUMN_WIDTH: 15, // mm
  TITLE_BOX_HEIGHT: 12, // mm
  TITLE_SPACING: 5, // mm
  TABLE_SPACING: 15, // mm
  STANDARD_WIDTH: 1400, // px
  MIN_WIDTH_THRESHOLD: 800, // px
  RENDER_DELAY: 500, // ms
  CANVAS_SCALE: 2,
};

export const PDF_TABLE_STYLES = {
  HEAD: {
    fillColor: [248, 250, 252],
    textColor: [31, 41, 55],
    fontStyle: 'bold',
    fontSize: 9,
    cellPadding: 3,
  },
  BODY: {
    textColor: [55, 65, 81],
    fontSize: 8,
    cellPadding: 3,
  },
  ALTERNATE_ROW: {
    fillColor: [248, 250, 252],
  },
  BASE: {
    fontSize: 8,
    cellPadding: 3,
    overflow: 'linebreak',
    cellWidth: 'wrap',
    lineWidth: 0.1,
    lineColor: [226, 232, 240],
  },
};

export const REPORT_FILTER_LABELS = {
  overdue: 'Overdue',
  today: 'Due Today',
  week: 'Due This Week',
};

export const REPORT_FIELD_LABEL_MAP = {
  task_type: 'Task Type',
  labels: 'Label',
  assignee: 'Assignee',
  priority: 'Priority',
  status: 'Status',
};

// Strategist Agent Constants
export const AGENT_OUTPUT_LEVELS = {
  EPIC: 'epic',
  FEATURE: 'feature',
  TASK: 'task',
  SUBTASK: 'subtask',
};

export const STRATEGIST_MODEL_SETTINGS = {
  KEY: 'STRATEGIST_AGENT_CONFIGURATION',
  FORM_FIELDS: {
    PROVIDER: 'provider',
    VERSION: 'version'
  },
  AI_PROVIDER_OPTIONS: {
    ChatGPT: {
      models: [
        'gpt-5.6-sol',
        'gpt-5.6-terra',
        'gpt-5.6-luna',
        'gpt-5.2',
        'gpt-5.1',
        'gpt-5',
        'gpt-5-mini',
        'gpt-5-nano',
        'gpt-5-pro',
        'gpt-4',
        'gpt-4o-mini',
        'gpt-4o',
        'gpt-4-turbo'
      ],

      type: 'Large Language Model (LLM)'
    }
  }
};

export const AC_FORMATS = {
  GIVEN_WHEN_THEN: 'given-when-then',
  BULLET: 'bullet',
  CHECKLIST: 'checklist',
};

export const AI_HTML_CLASSES = {
  ACCEPTANCE_CRITERIA_BODY: 'ai-acceptance-criteria-body',
};

export const GWT_KEYWORDS = {
  GIVEN: 'GIVEN',
  WHEN: 'WHEN',
  THEN: 'THEN',
  AND: 'AND',
  BUT: 'BUT'
};

export const AI_GWT_STYLES = {
  GIVEN: {
    bg: 'rgba(59, 130, 246, 0.15)',
    color: 'rgba(59, 130, 246, 0.9)',
    border: '1px solid rgba(59, 130, 246, 0.2)'
  },
  WHEN: {
    bg: 'rgba(168, 85, 247, 0.15)',
    color: 'rgba(168, 85, 247, 0.9)',
    border: '1px solid rgba(168, 85, 247, 0.2)'
  },
  THEN: {
    bg: 'rgba(16, 185, 129, 0.15)',
    color: 'rgba(16, 185, 129, 0.9)',
    border: '1px solid rgba(16, 185, 129, 0.2)'
  },
  DEFAULT: {
    bg: 'rgba(71, 85, 105, 0.1)',
    color: 'rgba(71, 85, 105, 0.9)',
    border: '1px solid rgba(71, 85, 105, 0.2)'
  }
};

export const REPORT_TYPES = {
  SPRINT_SUMMARY: 'project_summary',
  PROJECT_STATUS: 'project_status',
  BLOCKERS: 'blockers',
  VELOCITY: 'velocity',
  CUSTOM: 'custom',
};

// Project Status Constants
export const PROJECT_STATUS = {
  ACTIVE: 'ACTIVE',
  ON_HOLD: 'ON HOLD',
  INACTIVE: 'INACTIVE',
  COMPLETED: 'COMPLETED'
};

// Project Status Modal Config
export const PROJECT_STATUS_MODAL_CONFIG = {
  [PROJECT_STATUS.ON_HOLD]: {
    icon: 'PauseCircle',
    colorClass: 'warning',
    titleKey: 'projects.projectOnHoldTitle',
    messageKey: 'projects.projectOnHoldMessage',
    actionKey: 'projects.activateProject',
    loadingKey: 'projects.activatingProject'
  },
  [PROJECT_STATUS.INACTIVE]: {
    icon: 'RemoveCircleOutline',
    colorClass: 'inactive',
    titleKey: 'projects.projectInactiveTitle',
    messageKey: 'projects.projectInactiveMessage',
    actionKey: 'projects.activateProject',
    loadingKey: 'projects.activatingProject'
  },
  [PROJECT_STATUS.COMPLETED]: {
    icon: 'CheckCircle',
    colorClass: 'success',
    titleKey: 'projects.projectCompletedTitle',
    messageKey: 'projects.projectCompletedMessage',
    actionKey: 'projects.reactivateProject',
    loadingKey: 'projects.reactivatingProject'
  }
};

// Project Alert Constants
export const ALERT_CONDITION_TYPES = {
  PAST_DUE_DATE: 'past_due_date',
  DUE_IN_X_DAYS: 'due_in_x_days',
  STUCK_IN_PROGRESS: 'stuck_in_progress',
  HIGH_PRIORITY_OVERDUE: 'high_priority_overdue',
  NO_ACTIVITY_FOR_X_DAYS: 'no_activity_for_x_days',
  DEPENDENCY_BLOCKED: 'dependency_blocked',
  STUCK_IN_REVIEW: 'stuck_in_review'
};

export const ALERT_ACTION_TYPES = {
  EMAIL: 'email',
  SLACK: 'slack'
};

export const ALERT_STATUS = {
  ENABLED: 'enabled',
  DISABLED: 'disabled'
};

export const ALERT_THRESHOLD_OPTIONS = [
  '4 hours',
  '1 day',
  '3 days',
  '5 days',
  '7 days',
  '10 days'
];

// Ticket Category Constants
export const TICKET_CATEGORIES = {
  TYPE: 'type',
  PRIORITY: 'priority',
  LABELS: 'labels',
  SPRINT: 'sprint',
  STATUS: 'status'
};

export const TICKET_CATEGORY_NAMES = {
  [TICKET_CATEGORIES.TYPE]: 'Task type',
  [TICKET_CATEGORIES.PRIORITY]: 'Priority',
  [TICKET_CATEGORIES.LABELS]: 'Label',
  [TICKET_CATEGORIES.SPRINT]: 'Sprint',
  [TICKET_CATEGORIES.STATUS]: 'Status'
};

// Route Constants
export const ROUTES_PATHS = {
  STRATEGIST_AGENT: '/strategist-agent',
  BACKLOG_MANAGEMENT: '/backlog-management',
  SPRINT_TIMELINE: '/sprint-timeline',
  SPRINT_MANAGEMENT: '/sprint-management',
  DAILY_STANDUP: '/daily-standup'
};

// Sprint Timeline Constants
export const SPRINT_TIMELINE_STATUS_COLORS = {
  DRAFT: '#666666',
  ACTIVE: '#51861B',
  COMPLETED: '#082B72'
};

export const SCRUM_SPRINT_STATES = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED'
};

export const SCRUM_BACKLOG_SENTINEL = 'backlog';

export const SPRINT_TICKET_LIST_TYPES = {
  ALL: 'all',
  BLOCKED: 'blocked'
};

// File Upload Constants
export const FILE_UPLOAD = {
  // Accepted MIME type prefix for clipboard image validation
  IMAGE_MIME_PREFIX: 'image/',
  // Maximum file size in bytes (must match AZURE_STORAGE.MAX_FILE_SIZE on backend)
  MAX_SIZE_BYTES: 20 * 1024 * 1024,
  // Human-readable size label shown in error messages
  MAX_SIZE_LABEL: '20MB'
};

// Rich Text Formatting Constants
export const FORMATTING_COLORS = [
  { key: 'default', value: '#1f2937' },
  { key: 'gray', value: '#6b7280' },
  { key: 'blue', value: '#3b82f6' },
  { key: 'indigo', value: '#6366f1' },
  { key: 'purple', value: '#8b5cf6' },
  { key: 'pink', value: '#ec4899' },
  { key: 'red', value: '#ef4444' },
  { key: 'orange', value: '#f97316' },
  { key: 'green', value: '#10b981' },
  { key: 'cyan', value: '#06b6d4' }
];

// Calendar View Constants
export const CALENDAR_VIEW_TYPES = {
  MONTH: 'Month',
  WEEK: 'Week',
  CUSTOM: 'Custom'
};

// Date Picker Constants
export const DATE_PICKER_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const DATE_PICKER_SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const DATE_PICKER_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const WEEK_DAYS_SINGLE_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
export const WEEK_DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const DATE_PICKER_RANGE_LABELS = {
  FROM: 'From date',
  TO: 'To date'
};

export const DATE_PICKER_QUICK_OPTIONS = {
  TODAY: 'Today',
  TOMORROW: 'Tomorrow',
  NEXT_WEEK: 'Next Week',
  TWO_WEEKS: '2 weeks',
  FOUR_WEEKS: '4 weeks',
  CUSTOM: 'Custom'
};

export const TIME_FORMATS = {
  AM: 'AM',
  PM: 'PM'
};

export const DATE_PICKER_BACK_TO_LIST = 'Back to list';

export const DATE_DISPLAY_LABELS = {
  WEEK: 'Week',
  MONTH: 'Month',
  QUARTER: 'Quarter',
  YEAR: 'Year'
};

export const DATE_PICKER_SELECT_YEAR = 'Select Year';

export const DATE_PICKER_QUARTERS = [
  { label: 'First Quarter', months: [DATE_PICKER_SHORT_MONTHS[0], DATE_PICKER_SHORT_MONTHS[1], DATE_PICKER_SHORT_MONTHS[2]] },
  { label: 'Second Quarter', months: [DATE_PICKER_SHORT_MONTHS[3], DATE_PICKER_SHORT_MONTHS[4], DATE_PICKER_SHORT_MONTHS[5]] },
  { label: 'Third Quarter', months: [DATE_PICKER_SHORT_MONTHS[6], DATE_PICKER_SHORT_MONTHS[7], DATE_PICKER_SHORT_MONTHS[8]] },
  { label: 'Fourth Quarter', months: [DATE_PICKER_SHORT_MONTHS[9], DATE_PICKER_SHORT_MONTHS[10], DATE_PICKER_SHORT_MONTHS[11]] }
];

// Gantt View Constants
export const GANTT_VIEW_TYPES = {
  DAY: 'Day',
  WEEK: 'Week',
  QUARTER: 'Quarter',
  YEAR: 'Year'
};

export const GANTT_TASK_LIST = {
  ITEMS_PER_PAGE: 10,
};

export const CALENDAR_COLOR_OPTIONS = [
  { id: 'status', label: 'Colour code by Status' },
  { id: 'priority', label: 'Colour code by Priority' },
  { id: 'taskType', label: 'Colour code by Task Type' }
];

// Priority Constants
export const TASK_PRIORITY = {
  UNASSIGNED: 'Unassigned',
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

export const DEFAULT_PRIORITY_ORDER = ['Critical', 'High', 'Medium', 'Low', 'Unassigned'];

export const CALENDAR_PRIORITY_NAMES = {
  UNASSIGNED: 'Unassigned',
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

// Shared UI and Theme Constants
export const UI = {
  newTaskHighlight: '#F0F7FF',
  newTaskHighlightHover: '#E8F2FE',
  unassignedPriorityColor: '#94a3b8',
  screenshot: {
    activeBorder: '#0c66e4',
    badgeBg: '#172b4d',
    handleWidth: 8,
    handleHeight: 48,
    handleHoverBorder: '#0052cc',
    borderWidth: 1.5,
    minWidth: 130,
    resizeBoxShadow: '0 1px 4px rgba(9, 30, 66, 0.25)',
    captionPadding: '4px 8px',
    captionFontSize: 13
  },
  animationMs: {
    highlight: 300,
    newlyCreatedTaskDuration: 6000
  },
  shadowOpacity: {
    subtle: 0.05,
    light: 0.15,
    medium: 0.25,
    deep: 0.35
  },
  dimensions: {
    avatarSizeSmall: 24,
    avatarSizeMedium: 32,
    avatarSizeLarge: 48,
    spacingXs: 4,
    spacingSm: 8,
    spacingMd: 16,
    spacingLg: 24,
    spacingXl: 48
  }
};

export const CALENDAR_PRIORITY_COLOR_MAP = {
  [CALENDAR_PRIORITY_NAMES.CRITICAL]: '#dc2626',
  [CALENDAR_PRIORITY_NAMES.HIGH]: '#ea580c',
  [CALENDAR_PRIORITY_NAMES.MEDIUM]: '#f59e0b',
  [CALENDAR_PRIORITY_NAMES.LOW]: '#fbbf24',
  [CALENDAR_PRIORITY_NAMES.UNASSIGNED]: UI.unassignedPriorityColor
};

// Calendar Status Keywords
export const CALENDAR_STATUS_KEYWORDS = {
  TODO: ['todo', 'to do', 'open', 'not started'],
  IN_PROGRESS: ['progress', 'in progress', 'doing', 'on track'],
  REVIEW: ['review', 'on hold', 'pending', 'overdue'],
  DONE: ['done', 'closed', 'complete'],
  BLOCKED: ['blocked']
};

export const CALENDAR_STATUS_COLOR_MAP = {
  TODO: '#64748b',
  IN_PROGRESS: '#0ea5e9', // Changed from #3b82f6 to avoid conflict
  REVIEW: '#f59e0b',
  DONE: '#10b981',
  BLOCKED: '#ef4444'
};

// Calendar Task Type Keywords
export const CALENDAR_TASK_TYPE_KEYWORDS = {
  BUG: ['bug'],
  FEATURE: ['feature'],
  TASK: ['task'],
  SUBTASK: ['subtask', 'sub-task', 'sub task', 'sub-tasks'],
  STORY: ['story'],
  EPIC: ['epic']
};

export const CALENDAR_TASK_TYPE_COLOR_MAP = {
  BUG: '#ef4444',
  FEATURE: '#10b981',
  TASK: '#06b6d4', 
  SUBTASK: '#8b5cf6',
  STORY: '#ec4899',
  EPIC: '#f59e0b'
};

export const CALENDAR_COLOR_OPTION_LABELS = {
  priority: 'Priority',
  status: 'Status',
  taskType: 'Task Type'
};

// Calendar Autocomplete Constants
export const AUTOCOMPLETE_REASONS = {
  RESET: 'reset',
  CLEAR: 'clear'
};

// Calendar Color Code Options
export const COLOR_CODE_OPTIONS = {
  STATUS: 'status',
  PRIORITY: 'priority',
  TASK_TYPE: 'taskType'
};

export const BOARD_COLUMN_PRESET_COLORS = [
  '#9CA3AF', '#AACBFF', '#FFD1D1', '#F59E0B',
  '#6FC812', '#6366F1', '#A855F7', '#14B8A6',
  '#F472B6', '#FB923C', '#34D399', '#60A5FA',
];

// Gantt Settings Constants
export const GANTT_SETTINGS_SECTIONS = {
  CONFIGURATIONS: 'configurations',
  GROUP_BY: 'groupBy',
  COLOUR_CODE: 'colourCode'
};

export const GANTT_CONFIG_OPTIONS = {
  DEPENDENCIES: 'dependencies',
  TODAY_INDICATOR: 'todayIndicator'
};

export const GANTT_GROUP_BY_OPTIONS = {
  TASK_TYPE: 'taskType',
  ASSIGNEE: 'assignee',
  PRIORITY: 'priority',
  STATUS: 'status'
};

// Gantt Dependency Constants
export const GANTT_DEPENDENCY_COLORS = {
  BLOCKS: '#E74C3C',
  DEPENDENT_ON: '#9B59B6',
  REQUIRED_FOR: '#9B59B6',
  PARENT_OF: '#3F51B5'
};

export const GANTT_DEPENDENCY_TYPES = {
  BLOCKS: 'Blocks',
  REQUIRED_FOR: 'Required For'
};

export const GANTT_DEPENDENCY_LABELS = {
  ...GANTT_DEPENDENCY_TYPES,
  DEPENDENT_ON: 'Dependent On',
  BLOCKED_BY: 'Blocked By'
};

export const GANTT_DEPENDENCY_INVERSES = {
  [GANTT_DEPENDENCY_LABELS.BLOCKS]: GANTT_DEPENDENCY_LABELS.BLOCKED_BY,
  [GANTT_DEPENDENCY_LABELS.REQUIRED_FOR]: GANTT_DEPENDENCY_LABELS.DEPENDENT_ON,
  [GANTT_DEPENDENCY_LABELS.DEPENDENT_ON]: GANTT_DEPENDENCY_LABELS.REQUIRED_FOR
};

export const normalizeDependencyType = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

// UI Layout Constants
export const UI_CONSTANTS = {
  BORDER_RADIUS: {
    SMALL: '4px',
    MEDIUM: '8px',
    LARGE: '12px',
    FULL: '9999px'
  },
  SPACING: {
    XS: '4px',
    SM: '8px',
    MD: '16px',
    LG: '24px',
    XL: '32px'
  },
  HEIGHT: {
    FILTER_INPUT: 40,
    TOTAL_DISPLAY: 40,
    NAVBAR: 64
  },
  SHADOWS: {
    CARD: '0 4px 12px rgba(0, 0, 0, 0.05)',
    DROPDOWN: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    PRIMARY: '0 4px 12px rgba(59, 130, 246, 0.25)'
  }
};

// History Field Constants
export const HISTORY_FIELDS = {
  CHECKLIST: 'checklist',
  ATTACHMENTS: 'attachments',
  SUBTASK: 'subtask',
  SUBTASK_REMOVED: 'subtask_removed',
  ESTIMATE_TIME: 'estimate_time_in_minutes',
  START_DATE: 'start_date',
  DUE_DATE: 'due_date'
};

export const HISTORY_ACTION_TYPES = {
  UPDATED: 'updated',
  ASSIGNEE_ADDED: 'assignee_added',
  ASSIGNEE_REMOVED: 'assignee_removed',
  BULK_UPDATED: 'bulk_updated'
};

export const CHECKLIST_CHANGE_TYPES = {
  ADD_ITEM: 'add_item',
  UPDATE_ITEM: 'update_item',
  DELETE_ITEM: 'delete_item',
  DELETE_CHECKLIST: 'delete_checklist',
  CLEAR_ALL: 'clear_all',
  REORDER: 'reorder',
  REPLACE_ALL: 'replace_all'
};

// Auto-save Constants
export const AUTO_SAVE = {
  DEBOUNCE_MS: 1000,
  PREFIX: 'pmpi_draft_',
  EXPIRY_MS: 24 * 60 * 60 * 1000 // 24 hours
};

// Local Storage Key Constants
export const TIMESHEET_STORAGE_KEYS = {
  FILTER_VALUES: 'timesheetFilterValues',
  FILTERS_COLLAPSED: 'timesheetFiltersCollapsed'
};

export const STORAGE_KEYS = {
  DASHBOARD_FILTER_VALUES: 'dashboardFilterValues',
  PROJECT_STATUS_FILTER: 'projectManagement.statusFilter',
  PROJECT_COLLAPSED_SPACES: 'projectManagement.collapsedSpaces',
  USER_STATUS_FILTER: 'userManagement.statusFilter'
};

export const MANAGEMENT_FILTER_STATUS = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

// Backlog Constants
export const BACKLOG_CARD_TYPES = {
  ACTIVE_SPRINT: 'active-sprint',
  PLANNED_SPRINT: 'planned-sprint',
  MY_TICKETS: 'my-tickets'
};

export const SPRINT_TIMELINE_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  DRAFT: 'draft'
};

export const SPRINT_STATUSES = {
  ACTIVE: 'active',
  PLANNED: 'planned',
  COMPLETED: 'completed'
};

export const SPRINT_MANAGEMENT_CONSTANTS = {
  RISK_LEVELS: {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
    NOT_APPLICABLE: '-'
  },
  UTILISATION_STATUS: {
    OVER: 'over',
    UNDER: 'under',
    OPTIMAL: 'optimal'
  },
  HEALTH_STATUS: {
    ON_TRACK: 'on-track',
    AT_RISK: 'at-risk',
    DRAFT: 'draft'
  },
  API_STATE: {
    ON_TRACK: 'On Track',
    AT_RISK: 'At Risk',
    DRAFT: 'Draft'
  },

  LOAD_LEVELS: {
    OVER_UTILISED: 'Over-Utilised',
    OPTIMAL: 'Optimal',
    UNDER_UTILISED: 'Under-Utilised'
  }
};

export const RESTRICTED_NUMBER_KEYS = ['.', 'e', 'E', '+', '-'];

// Saved board filter preferences (user-wide, including project context).
export const SAVED_FILTERS = {
  PREFERENCE_KEY: 'board_saved_filters',
  NAME_MAX_LENGTH: 100,
  OPERATIONS: { CREATE: 'create', UPDATE: 'update', RENAME: 'rename', DELETE: 'delete' }
};
